/* eslint-disable camelcase */
// @ts-check

import { TCString } from '@mailonline/tcf-core'
import { createLog } from '../utils/logger'
import * as TcModelFactory from './TcModelFactory'
import {
  TCFVersion2_2_SupportedPurposeLI,
  isTCFVersion2_2
} from '../utils/policyVersion'
import { consentOrPayDisplayPurposes } from '../ui/display-purposes'

const log = createLog()

export class TcModel {
  /**
   * @param {TcModelOpts} opts
   */
  constructor({ gvl, molGvlVersion, model, specialPurposeOnlyDeclared = [] }) {
    this.gvl = gvl
    /**
     * @type {TCModel}
     */
    this.model = model
    this.molGvlVersion = molGvlVersion.replace(/\//g, '.')
    this.specialPurposeOnlyDeclared = specialPurposeOnlyDeclared
    this.byPurposeVendorMap = this.byPurposeVendorMap.bind(this)
    this.gvlIsReady = this.gvlIsReady.bind(this)
    this.updated = this.updated.bind(this)
    this.tcString = this.tcString.bind(this)
    this.updateUserPreferences = this.updateUserPreferences.bind(this)
    this.consentAll = this.consentAll.bind(this)
    this.dissentAll = this.dissentAll.bind(this)
    this.hasUserDissentedToAll = this.hasUserDissentedToAll.bind(this)
    this.hasUserConsentedToAll = this.hasUserConsentedToAll.bind(this)
    this.consentedVendorCheck = this.consentedVendorCheck.bind(this)
  }

  reload(tcString) {
    const [newModel, specialPurposeOnlyDeclared] =
      TcModelFactory.createModelFromTcString(this.gvl, tcString)
    log('reloading tcModel', tcString, newModel)

    this.model = newModel
    this.specialPurposeOnlyDeclared = specialPurposeOnlyDeclared
  }

  byPurposeVendorMap() {
    return this.model.gvl.byPurposeVendorMap
  }

  gvlIsReady() {
    return this.model.gvl.readyPromise
  }

  updated() {
    this.model.updated()
  }

  tcString() {
    log('encoding', this.model)
    return TCString.encode(this.model)
  }

  updateUserPreferences({
    vendorConsents,
    vendorLegitimateInterests,
    purposeConsents,
    purposeLegitimateInterests,
    specialFeatureOptins,
    consentScreen,
    created,
    lastUpdated
  }) {
    this.model.vendorConsents.empty()
    this.model.vendorConsents.set(vendorConsents)

    this.model.vendorLegitimateInterests.empty()
    this.model.vendorLegitimateInterests.set(vendorLegitimateInterests)
    this.model.vendorLegitimateInterests.set(
      this.specialPurposeOnlyDeclared.map(({ id }) => id)
    )

    this.model.purposeConsents.empty()
    this.model.purposeConsents.set(purposeConsents)
    this.model.purposeLegitimateInterests.empty()
    this.model.purposeLegitimateInterests.set(
      purposeLegitimateInterests.filter(
        pli => TCFVersion2_2_SupportedPurposeLI.includes(pli)
      )
    )

    this.model.specialFeatureOptins.empty()
    this.model.specialFeatureOptins.set(specialFeatureOptins)

    this.model.consentScreen = consentScreen

    this.model.updated()

    if (created) {
      this.model.created = created
    }

    if (lastUpdated) {
      this.model.lastUpdated = lastUpdated
    }

    this.model = TCString.decode(TCString.encode(this.model))
    this.model.gvl = this.gvl
  }

  consentAll({ consentScreen }) {
    this.model.setAll()
    if (isTCFVersion2_2(this.model.gvl.tcfPolicyVersion)) {
      this.model.unsetAllPurposeLegitimateInterests()
      this.model.purposeLegitimateInterests.set(
        TCFVersion2_2_SupportedPurposeLI
      )
    }
    this.model.consentScreen = consentScreen
    this.model.updated()
    this.model = TCString.decode(TCString.encode(this.model))
    this.model.gvl = this.gvl
  }

  dissentAll({ consentScreen }) {
    this.model.unsetAll()
    if (isTCFVersion2_2(this.model.gvl.tcfPolicyVersion)) {
      this.model.unsetAllPurposeLegitimateInterests()
      this.model.purposeLegitimateInterests.set(
        TCFVersion2_2_SupportedPurposeLI
      )
    } else {
      this.model.setAllPurposeLegitimateInterests()
    }
    this.model.setAllVendorLegitimateInterests()
    this.model.consentScreen = consentScreen
    this.model.updated()
    this.model = TCString.decode(TCString.encode(this.model))
    this.model.gvl = this.gvl
  }

  hasUserDissentedToAll() {
    return (
      !this.model.specialFeatureOptins.size &&
      !this.model.purposeConsents.size &&
      !this.model.vendorConsents.size
    )
  }

  hasUserConsentedToAll() {
    return (
      this.#hasUserConsentedToAllVendors() &&
      this.#hasUserConsentedToAllPurposes() &&
      this.#hasConsentedToAllSpecialFeatureOptins()
    )
  }

  hasUserConsentedToMandatory() {
    const purposesFromDisplayPurposes = consentOrPayDisplayPurposes
      .filter(({ type }) => type === 'purposes')
      .map(({ id }) => id)
    const immutableStacksFromDisplayPurposes =
      consentOrPayDisplayPurposes.filter(
        ({ type, isConsentImmutable }) =>
          type === 'stacks' && isConsentImmutable
      )
    const purposesFromStacks = immutableStacksFromDisplayPurposes
      .map(({ id }) => this.model.gvl.stacks?.[id]?.purposes)
      .flat()

    const consentedToAllPurposes = [
      ...purposesFromDisplayPurposes,
      ...purposesFromStacks
    ].every((id) => this.model.purposeConsents.has(id))
    return (
      this.#hasUserConsentedToAllVendors() &&
      consentedToAllPurposes &&
      this.#hasConsentedToAllSpecialFeatureOptins()
    )
  }

  #hasUserConsentedToAllVendors() {
    const vendorsWithPurposes = Object.keys(this.model.gvl.vendors)
      .filter((id) => this.model.gvl.vendors[id].purposes.length > 0)
      .map((id) => parseInt(id))

    return vendorsWithPurposes.every((id) => this.model.vendorConsents.has(id))
  }

  #hasUserConsentedToAllPurposes() {
    const purposesIds = Object.keys(this.model.gvl.purposes).map((id) =>
      parseInt(id)
    )
    return purposesIds.every((id) => this.model.purposeConsents.has(id))
  }

  #hasConsentedToAllSpecialFeatureOptins() {
    const specialFeatureOptinIds = Object.keys(
      this.model.gvl.specialFeatures
    ).map((id) => parseInt(id))
    return specialFeatureOptinIds.every((id) =>
      this.model.specialFeatureOptins.has(id)
    )
  }

  consentedVendorCheck() {
    const purposesIds = Object.keys(this.model.gvl.purposes).map((id) =>
      parseInt(id)
    )
    const purposeRestrictions =
      this.model.publisherRestrictions.getRestrictions()
    const model = TCString.decode(TCString.encode(this.model))
    model.gvl = this.gvl
    const vendorIdsDissentedByPurpose = new Set(
      purposesIds
        .filter((id) => !model.purposeConsents.has(id))
        .map((id) =>
          Array.from(
            (this.model.gvl.byPurposeVendorMap[id] &&
              this.model.gvl.byPurposeVendorMap[id].consent) ||
            []
          )
        )
        .flat()
    )
    const vendorIdDissentedByFlexiblePurpose = (id) => {
      return purposeRestrictions.some((restriction) => {
        return (
          model.publisherRestrictions.vendorHasRestriction(id, restriction) &&
          !model.purposeConsents.has(restriction.purposeId)
        )
      })
    }
    const preferenceForIds = Array.from(this.model.gvl.vendorIds).map((id) => {
      const constrainedByFlexPurpose = vendorIdDissentedByFlexiblePurpose(id)
      const vendorRestrictedToConsent = purposeRestrictions.some(
        (restriction) =>
          model.publisherRestrictions.vendorHasRestriction(id, restriction)
      )
      const hasConsented =
        (vendorRestrictedToConsent
          ? model.vendorConsents.has(id)
          : model.vendorConsents.has(id) ||
          model.vendorLegitimateInterests.has(id)) &&
        !vendorIdsDissentedByPurpose.has(id) &&
        !constrainedByFlexPurpose

      return {
        id,
        hasConsented,
        consentedTests: {
          consented: model.vendorConsents.has(id),
          legitimateInterest: model.vendorLegitimateInterests.has(id),
          vendorRestrictedToConsent,
          dissentedByPurpose: vendorIdsDissentedByPurpose.has(id),
          constrainedByFlexPurpose
        }
      }
    })
    return preferenceForIds
  }

  consentedVendors() {
    return this.consentedVendorCheck()
      .filter(({ hasConsented }) => hasConsented)
      .map(({ id }) => id)
  }

  consentedVendorsDetails() {
    return this.consentedVendorCheck()
  }
}

/**
 * @typedef {Object} TcModelOpts
 * @property {GVL} gvl
 * @property {string} molGvlVersion
 * @property {TCModel} model
 * @property {Array<import('@mailonline/tcf-core/lib/mjs/model/gvl/Vendor')>} specialPurposeOnlyDeclared
 */

/**
 * @typedef {import('@mailonline/tcf-core').TCModel} TCModel
 */

/**
 * @typedef {import('@mailonline/tcf-core').GVL} GVL
 */
